import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Address, countryDefaultValue } from '../../../../models/address/address';
import { CompanyChangeAddress, CompanyForm362 } from '../../../models/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { AppointCompanyStepsEnum } from './AppointCompanyStepsEnum';
import { IStep } from '@models/ui-models/step';
import { autocompleteServiceToken } from '../../../../shared/services/autocomplete.service';
import { AuxiliaryApiService } from '@shared/services/auxiliary-api.service';
import {
  AddressAndOccupierFormGroupComponent
} from "../../../reusable-form-groups/address-and-occupier-form-group/address-and-occupier-form-group.component";
import {
  CompanyNameAcnFormGroupComponent
} from "../../../reusable-form-groups/company-name-acn-form-group/company-name-acn-form-group.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { Company } from "@models/companies/company";
import { ExternalSourceType } from "@models/integrations/external-source-type.enum";

@Component({
  selector: 'app-company-form-362',
  standalone: true,
  templateUrl: './company-form-362.component.html',
  styleUrls: ['../../stepper-form/base-stepper-component/base-stepper-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    StepperFormComponent,
    StepperFormDescriptionComponent,
    YesNoControlComponent,
    CompanyNameAcnFormGroupComponent,
    AddressAndOccupierFormGroupComponent
  ],
  providers: [{ provide: autocompleteServiceToken, useClass: AuxiliaryApiService }],
})
export class CompanyForm362Component extends BaseStepperFormComponent<AppointCompanyStepsEnum, CompanyForm362> implements OnInit {
  @Input() address?: Address; // pass as input prop to address component for changes

  override readonly StepsEnum = AppointCompanyStepsEnum;
  readonly allowedSources: ExternalSourceType[] = [];

  expandedAddressForm = false;

  override stepperForm = new FormGroup({
    [AppointCompanyStepsEnum.FormDescription]: new FormGroup({}),
    [AppointCompanyStepsEnum.CompanyInformation]: new FormGroup({
      ...CompanyNameAcnFormGroupComponent.defineFormControls(),
      needToChangeRegisteredAddress: new FormControl<boolean | null>(false, [Validators.required]),
      // needToChangeRegisteredAddress: new FormControl<boolean | null>(null, [Validators.required]),
    }),
    [AppointCompanyStepsEnum.NewAddressInformation]: AddressAndOccupierFormGroupComponent.defineForm(),
  });

  stepsSignal: WritableSignal<IStep<AppointCompanyStepsEnum>[]> = signal([]);

  constructor() {
    super();
    this.setupSteps(AppointCompanyStepsEnum);
    this.redirectAfterSubmit = true;
  }

  ngOnInit(): void {
    this.updateFormSteps();
    this.listenNeedToChangeRegisteredAddress();
    this.setupChange();
  }

  override setupChange(change: CompanyForm362 = this.formModel): void {
    this.addressForm.controls.address.patchValue({ country: countryDefaultValue });
    if (!this.isEdit && change === this.formModel)
      return;

    this.companyInformationForm.patchValue({
      name: change.companyName,
      acn: change.companyACN,
      needToChangeRegisteredAddress: false
    });
  }

  override setCurrentStep(newStepIndex: number) {
    const firstInvalidFormStep = this.stepsSignal()
      .slice(0, newStepIndex)
      .find(step => this.stepperForm.controls[step.step].invalid);

    this.currentStep = this.stepsSignal()[newStepIndex].step;
    this.currentStepIndex = newStepIndex;

    if (firstInvalidFormStep?.step === AppointCompanyStepsEnum.NewAddressInformation) {
      this.expandedAddressForm = true;
    }
  }

  override buildDocument(): Document | null {
    const changes: EntityChangeData[] = [];
    const companyAppointChange = new CompanyForm362({
      companyName: this.companyInformationForm.controls.name.value!,
      companyACN: this.companyInformationForm.controls.acn.value!,
    });
    changes.push(companyAppointChange);

    if (this.companyInformationForm.controls.needToChangeRegisteredAddress.value) {
      const companyChangeAddress = new CompanyChangeAddress({
        ...this.addressForm.value as Partial<CompanyChangeAddress>,
        applyToRegistered: true,
        applyToPrincipal: false,
      });
      changes.push(companyChangeAddress);
    }

    try {
      return new Document({
        changes,
        entityId: this.companyChangeData?.entityId ?? null,
        type: 'c:362a',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  onCompanySelected(company: Company): void {
    this.companyInformationForm.patchValue({
      name: company?.name,
      acn: company?.acn,
    });
  }

  private listenNeedToChangeRegisteredAddress(): void {
    this.companyInformationForm.controls.needToChangeRegisteredAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((needToChangeRegisteredAddress) => this.updateFormSteps(needToChangeRegisteredAddress));
  }

  private updateFormSteps(needToChangeRegisteredAddress: boolean | null = false) {
    setControlDisabled(this.addressForm, !needToChangeRegisteredAddress);
    this.addressForm.controls.occupiesTheAddress?.patchValue(this.stepperForm.value[AppointCompanyStepsEnum.NewAddressInformation]?.occupiesTheAddress ?? true);

    this.stepsSignal.set(this.steps.filter((step) => {
      if (step.step === AppointCompanyStepsEnum.NewAddressInformation) {
        return needToChangeRegisteredAddress;
      }

      return true;
    }));
  }

  get companyInformationForm() {
    return this.stepperForm.controls[AppointCompanyStepsEnum.CompanyInformation];
  }

  get addressForm() {
    return this.stepperForm.controls[AppointCompanyStepsEnum.NewAddressInformation];
  }
}
